import * as Yup from 'yup';
import _last from 'lodash/last';
import _isString from 'lodash/isString';
import EditBillingInfoSchema from '../../components/shipment/Modals/EditBillingInformation.schema';
import { FORMACTIONS } from '../../constants/constants';
require('../../initializers/Yup');

const numberMax = 99999;
const numberMin = 0.01;
const maxDecimals = 4;
const valid = 'must be a valid';
const messages = {
  required: 'is required',
  invalid: 'is invalid',
  min: 'must be greater than 0',
  max: 'exceeded max size',
  number: `${valid} number`,
  validPhone: `${valid} phone number`,
  decimals: `exceeded ${maxDecimals} decimals`
};

const getElementNumber = text => {
  const regex = /\[(\d)\]/;
  const matches = text.match(regex);
  const elementNumber = Number(matches[1]) + 1;
  return elementNumber;
};
const formatUnitMessage = (unitFieldNames, field, message) => {
  const formFieldPath = field.path.split('.');
  const fieldName = unitFieldNames[_last(formFieldPath)];
  const prefix = `Unit ${getElementNumber(formFieldPath[0])} -`;
  return `${prefix} ${fieldName} ${message}`;
};
const formatProductMessage = (productFieldNames, field, message) => {
  const formFieldPath = field.path.split('.');
  const fieldName = productFieldNames[_last(formFieldPath)];
  const prefix = `Unit ${getElementNumber(
    formFieldPath[0]
  )} Product ${getElementNumber(formFieldPath[1])} - `;
  return `${prefix} ${fieldName} ${message}`;
};
export const validationSchema = ({ t }) => {
  const billToInfo = EditBillingInfoSchema({ t });

  const unitFieldNames = {
    length: t('Length'),
    width: t('Width'),
    height: t('Height'),
    weight: t('Weight'),
    packageTypeId: t('Package type'),
    freightClassId: t('Freight class'),
    unitCount: t('Quantity')
  };
  const productFieldNames = {
    name: t('Name'),
    packageTypeId: t('Package type'),
    pieceCount: t('Piece count'),
    quantity: t('Quantity'),
    freightClassId: t('Freight class'),
    weight: t('Weight'),
    value: t('Value'),
    properShippingName: t('HazMat Proper shipping name'),
    unNumber: t('HazMat UN Number'),
    hazMatClassId: t('HazMat class'),
    hazMatPackingGroupId: t('HazMat Packing group'),
    contactFirstName: t('HazMat contact first name'),
    contactLastName: t('HazMat contact last name'),
    contactPhone: t('HazMat contact phone')
  };
  const productSchema = buildProductField(t, productFieldNames);
  return Yup.object().shape({
    //   proNumber: Yup.number().typeError(t('Pro Number must be a valid number')),
    glCodeId: Yup.string()
      .typeError(t('GL Code is required'))
      .nullable(),
    proNumber: Yup.string().nullable(),
    bolNumber: Yup.string().nullable(),
    carrierPickupNumber: Yup.string().nullable(),
    shipmentModeId: Yup.string().required('Shipment Type is required'),
    shipmentDirectionId: Yup.string()
      .typeError(t('Shipment Direction is required'))
      .required(t('Shipment Direction is required')),
    pickup: Yup.object()
      .shape({
        plannedToTimeLocal: Yup.string()
          .typeError(t('Pickup date is required'))
          .required(t('Pickup date is required'))
      })
      .concat(buildStopsField('Pickup', t)),
    billToInfo: billToInfo,
    totalLinearFeet: Yup.number()
      .transform(cv => (isNaN(cv) ? undefined : cv))
      .positive()
      .integer(t('Linear feet must be an integer'))
      .max(99, _field => t('Linear feet cannot exceed 99 ft'))
      .nullable(),
    shipmentStatusId: Yup.string(),
    lcpExceptionReasonId: Yup.string().when('isLCPExceptionRequired', {
      is: true,
      then: Yup.string()
        .required(t('LCP exception reason is required'))
        .typeError(t('LCP exception reason is required')),
      otherwise: Yup.string().nullable()
    }),
    references: Yup.array().of(
      Yup.lazy(value => {
        const responseText =
          value.maxResponses > 1 ? t('responses') : t('response');

        return Yup.object().shape({
          stringId: Yup.string(),
          required: Yup.boolean(),
          values: Yup.array().when('required', {
            is: true,
            then: Yup.array()
              .min(1, t('Reference field is required'))
              .max(
                value.maxResponses, // eslint-disable-next-line
                t('Reference field exceeded') + ' ${max} ' + responseText
              ), //string concat had to be done this way to keep $max from being interpolated

            otherwise: Yup.array()
              .nullable()
              .max(
                value.maxResponses, // eslint-disable-next-line
                t('Reference field exceeded') + ' ${max} ' + responseText
              )
          })
        });
      })
    ),
    isRateSelected: Yup.boolean().when('$formAction', (formAction, schema) => {
      return [FORMACTIONS.TENDER, FORMACTIONS.MANUALLY_TENDER].includes(
        formAction
      )
        ? schema
            .required(`${t('Selected rate')} ${messages.required}`)
            .typeError(`${t('Selected rate')} ${messages.required}`)
        : schema.nullable();
    }),
    delivery: buildStopsField('Delivery', t),
    handlingUnits: Yup.array().of(
      Yup.object().shape({
        packageTypeId: Yup.string().required(field =>
          formatUnitMessage(unitFieldNames, field, messages.required)
        ),
        unitCount: Yup.number()
          .typeError(field =>
            formatUnitMessage(unitFieldNames, field, messages.number)
          )
          .required(field =>
            formatUnitMessage(unitFieldNames, field, messages.required)
          )
          .min(numberMin, field =>
            formatUnitMessage(unitFieldNames, field, messages.min)
          )
          .max(numberMax, field =>
            formatUnitMessage(unitFieldNames, field, messages.max)
          )
          .decimals(maxDecimals, field =>
            formatUnitMessage(unitFieldNames, field, messages.decimals)
          ),
        dimensionInfo: Yup.object().shape({
          length: Yup.number()
            .typeError(field =>
              formatUnitMessage(unitFieldNames, field, messages.number)
            )
            .required(field =>
              formatUnitMessage(unitFieldNames, field, messages.required)
            )
            .min(numberMin, field =>
              formatUnitMessage(unitFieldNames, field, messages.min)
            )
            .max(numberMax, field =>
              formatUnitMessage(unitFieldNames, field, messages.max)
            )
            .decimals(maxDecimals, field =>
              formatUnitMessage(unitFieldNames, field, messages.decimals)
            ),
          width: Yup.number()
            .typeError(field =>
              formatUnitMessage(unitFieldNames, field, messages.number)
            )
            .required(field =>
              formatUnitMessage(unitFieldNames, field, messages.required)
            )
            .min(numberMin, field =>
              formatUnitMessage(unitFieldNames, field, messages.min)
            )
            .max(numberMax, field =>
              formatUnitMessage(unitFieldNames, field, messages.max)
            )
            .decimals(maxDecimals, field =>
              formatUnitMessage(unitFieldNames, field, messages.decimals)
            ),
          height: Yup.number()
            .typeError(field =>
              formatUnitMessage(unitFieldNames, field, messages.number)
            )
            .required(field =>
              formatUnitMessage(unitFieldNames, field, messages.required)
            )
            .min(numberMin, field =>
              formatUnitMessage(unitFieldNames, field, messages.min)
            )
            .max(numberMax, field =>
              formatUnitMessage(unitFieldNames, field, messages.max)
            )
            .decimals(maxDecimals, field =>
              formatUnitMessage(unitFieldNames, field, messages.decimals)
            )
        }),
        actualWeightInfo: Yup.object().shape({
          weight: Yup.number()
            .typeError(field =>
              formatUnitMessage(unitFieldNames, field, messages.number)
            )
            .required(field =>
              formatUnitMessage(unitFieldNames, field, messages.required)
            )
            .min(1, field =>
              formatUnitMessage(unitFieldNames, field, 'must be greater than 1')
            )
            .max(numberMax, field =>
              formatUnitMessage(unitFieldNames, field, messages.max)
            )
            .decimals(maxDecimals, field =>
              formatUnitMessage(unitFieldNames, field, messages.decimals)
            )
        }),
        freightInfo: Yup.object().shape({
          freightClassId: Yup.string().required(field =>
            formatUnitMessage(unitFieldNames, field, messages.required)
          )
        }),
        shipmentItems: Yup.array().of(productSchema)
      })
    )
  });
};
const buildProductField = (t, productFieldNames) => {
  return Yup.object().shape({
    quantity: Yup.number()
      .typeError(field =>
        formatProductMessage(productFieldNames, field, messages.number)
      )
      .required(field =>
        formatProductMessage(productFieldNames, field, messages.required)
      )
      .min(1, field =>
        formatProductMessage(productFieldNames, field, messages.min)
      )
      .max(numberMax, field =>
        formatProductMessage(productFieldNames, field, messages.max)
      ),
    product: Yup.object().shape({
      name: Yup.string()
        .typeError(field =>
          formatProductMessage(productFieldNames, field, messages.required)
        )
        .required(field =>
          formatProductMessage(productFieldNames, field, messages.required)
        ),
      packageTypeId: Yup.string()
        .typeError(field =>
          formatProductMessage(productFieldNames, field, messages.required)
        )
        .required(field =>
          formatProductMessage(productFieldNames, field, messages.required)
        ),
      pieceCount: Yup.number()
        .typeError(field =>
          formatProductMessage(productFieldNames, field, messages.number)
        )
        .required(field =>
          formatProductMessage(productFieldNames, field, messages.required)
        )
        .min(1, field =>
          formatProductMessage(productFieldNames, field, messages.min)
        )
        .max(numberMax, field =>
          formatProductMessage(productFieldNames, field, messages.max)
        ),
      freightInfo: Yup.object().shape({
        freightClassId: Yup.string().required(field =>
          formatProductMessage(productFieldNames, field, messages.required)
        )
      }),
      weightInfo: Yup.object().shape({
        weight: Yup.number()
          .typeError(field =>
            formatProductMessage(productFieldNames, field, messages.number)
          )
          .required(field =>
            formatProductMessage(productFieldNames, field, messages.required)
          )
          .min(1, field =>
            formatProductMessage(
              productFieldNames,
              field,
              'must be greater than 1'
            )
          )
          .max(numberMax, field =>
            formatProductMessage(productFieldNames, field, messages.max)
          )
          .decimals(maxDecimals, field =>
            formatProductMessage(productFieldNames, field, messages.decimals)
          )
      }),
      valueInfo: Yup.object().shape({
        value: Yup.number()
          .transform(function(value, originalValue) {
            if (
              (_isString(originalValue) &&
                originalValue.toString().trim().length === 0) ||
              (!_isString(originalValue) && isNaN(originalValue))
            ) {
              return null;
            }
            return value;
          })
          .nullable()
          .typeError(field =>
            formatProductMessage(productFieldNames, field, messages.number)
          )
          .min(1, field =>
            formatProductMessage(
              productFieldNames,
              field,
              'must be greater than 1'
            )
          )
          .max(numberMax, field =>
            formatProductMessage(productFieldNames, field, messages.max)
          )
          .decimals(2, field =>
            formatProductMessage(
              productFieldNames,
              field,
              'exceeded 2 decimals'
            )
          )
      }),
      isHazMat: Yup.boolean(),
      hazMat: Yup.object().when('isHazMat', {
        is: true,
        then: Yup.object({
          properShippingName: Yup.string()
            .typeError(field =>
              formatProductMessage(productFieldNames, field, messages.number)
            )
            .required(field =>
              formatProductMessage(productFieldNames, field, messages.required)
            ),
          unNumber: Yup.string()
            .typeError(field =>
              formatProductMessage(productFieldNames, field, messages.number)
            )
            .required(field =>
              formatProductMessage(productFieldNames, field, messages.required)
            ),
          hazMatClassId: Yup.string()
            .typeError(field =>
              formatProductMessage(productFieldNames, field, messages.number)
            )
            .required(field =>
              formatProductMessage(productFieldNames, field, messages.required)
            ),
          hazMatPackingGroupId: Yup.string()
            .typeError(field =>
              formatProductMessage(productFieldNames, field, messages.number)
            )
            .required(field =>
              formatProductMessage(productFieldNames, field, messages.required)
            ),
          contactFirstName: Yup.string()
            .typeError(field =>
              formatProductMessage(productFieldNames, field, messages.number)
            )
            .required(field =>
              formatProductMessage(productFieldNames, field, messages.required)
            ),
          contactLastName: Yup.string()
            .typeError(field =>
              formatProductMessage(productFieldNames, field, messages.number)
            )
            .required(field =>
              formatProductMessage(productFieldNames, field, messages.required)
            ),
          contactPhone: Yup.string()
            .typeError(field =>
              formatProductMessage(productFieldNames, field, messages.required)
            )
            .phone(field =>
              formatProductMessage(
                productFieldNames,
                field,
                messages.validPhone
              )
            )
            .required(field =>
              formatProductMessage(productFieldNames, field, messages.required)
            )
        })
      })
    })
  });
};
const buildStopsField = (locationType, t) => {
  const isRequired = t('is required');
  const valid = t('must be a valid');
  const isInvalid = t('is invalid');
  const messages = {
    locationName: `${t(locationType)} - ${t('Location Name')} ${isRequired}`,
    address: `${t(locationType)} - ${t('Address')} ${isRequired}`,
    city: `${t(locationType)} - ${t('City')} ${isRequired}`,
    state: `${t(locationType)} - ${t('State')} ${isRequired}`,
    postalCode: `${t(locationType)} - ${t('Zip Code')} ${isRequired}`,
    country: `${t(locationType)} - ${t('Country')} ${isRequired}`,
    firstName: `${t(locationType)} - ${t('First name')} ${isRequired}`,
    lastName: `${t(locationType)} - ${t('Last name')} ${isRequired}`,
    email: `${t(locationType)} - ${t('Email')} ${isRequired}`,
    phone: `${t(locationType)} - ${t('Phone')} ${isRequired}`,
    validPhone: `${t(locationType)} - ${t('Phone')} ${valid} ${t('phone')}`,
    validPostalCode: `${t(locationType)} - ${t('Zip Code')} ${valid} ${t(
      'zip code'
    )}`,
    validEmail: `${t(locationType)} - ${t('Email')} ${valid} ${t('email')}`
  };

  return Yup.object().shape({
    openFromTimeLocal: Yup.number()
      .typeError(`${t(locationType)} - ${t('Dock open time')} ${isInvalid}`)
      .nullable(),
    openToTimeLocal: Yup.number()
      .typeError(`${t(locationType)} - ${t('Dock closing time')} ${isInvalid}`)
      .nullable(),
    locationName: Yup.string()
      .typeError(messages.locationName)
      .required(messages.locationName),
    contactInfo: Yup.object().shape({
      firstName: Yup.string()
        .typeError(messages.firstName)
        .required(messages.firstName),
      lastName: Yup.string()
        .typeError(messages.lastName)
        .required(messages.lastName),
      phone: Yup.string()
        .typeError(messages.phone)
        .phone(messages.validPhone)
        .required(messages.phone),
      email: Yup.string()
        .email(messages.validEmail)
        .typeError(messages.email)
        .required(messages.email)
    }),
    locationAddress: Yup.object().shape({
      address1: Yup.string()
        .typeError(messages.address)
        .required(messages.address),
      city: Yup.string()
        .typeError(messages.city)
        .required(messages.city),
      state: Yup.string()
        .typeError(messages.state)
        .required(messages.state),
      country: Yup.string()
        .typeError(messages.country)
        .required(messages.country),
      postalCode: Yup.string()
        .zip(messages.validPostalCode)
        .typeError(messages.postalCode)
        .required(messages.postalCode)
    })
  });
};
export default validationSchema;
